<template>
    <reo-dialog
        v-if="showSubscriptionDialog"
        :title="$t('ai.search.dialog.title')"
        :label-of-confirm="confirmLabel"
        :label-of-cancel="aiSearchCanWork? undefined : $t('cancel_plan.traffic.cancel')"
        @cancel="handleCloseDialog"
        @confirm="handleDialogConfirm"
        :disableConfirm="aiSearchCanWork && !agreement"
    >
        <div class="dialog-content">
            <div class="info">
                <p v-for="content in dialogContents" :key="String(content)"> {{ content }}</p>
                <button v-if="!showMoreInfo"
                    class="button_clear more-btn"
                    @click="showMoreInfo=true"
                >{{ $t('show_more') }}<i class="icon-co_add"/>
                </button>
                <reo-link
                    :href="config.ai_support"
                    :text="$t('e2ee.learn_more')"
                    target="_blank"
                    v-if="showMoreInfo"
                />
            </div>
            <div v-if="hasAiSubscription" class="agreement">
                <ReoCheckbox
                    v-model="agreement"
                    :isRound="false"
                />
                <span>
                    <span class="red">*</span>
                    {{ $t('ai.search.dialog.agreement') }}
                </span>
            </div>
            <p v-else-if="!hasAiSubscription" class="empty">
                {{ $t('ai.search.dialog.no_ai_sub') }}
            </p>
            <p v-else class="empty">
                {{ $t('ai.search.dialog.device_list_empty') }}
            </p>
        </div>
    </reo-dialog>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue';
import ReoDialog from '@/basic/Dialog.vue';
import ReoLink from '@/basic/Link.vue';
import { config } from 'CONFIG';
import {
    ESubscribeMode,
    ICloudService,
    ISubDeviceWithChecked,
    ISubscription
} from '@reolink-cloud/interface';
import { getSubDeviceList } from '@/api/subscriptions';
import { getDeviceTitle } from '@/utils/business';
import { SimManager, uidHandler } from '@/lib';

export default Vue.extend({
    components: {
        ReoDialog,
        ReoLink
    },
    props: {
        simManager: Object as PropType<SimManager>,
        showSubscriptionDialog: {
            type: Boolean,
            default: false
        },
        hasPlanExpired:{
            type: Boolean,
            default: false
        },
        disableConfirm: {
            type: Boolean,
            default: false
        },
        cancel:{
            type: Boolean,
            default: false
        },
        hasCurrentPlan:{
            type: Boolean,
            default: true,
        },
    },
    data() {

        return {
            deviceList: [] as ISubDeviceWithChecked[],
            available: false,
            agreement: false,
            showMoreInfo: false,
            subscriptions: [] as ISubscription[]
        };

    },
    computed:{
        isCountryAiOpen(): boolean {

            return config.aiFeatures.length > 0;

        },
        confirmLabel(): string {

            if ( this.hasPlanExpired ) {

                return this.$tc( 'subscribe.expired_subscribe_button' );

            } else if ( this.isCountryAiOpen ) {

                return this.$tc( 'update_plan.confirm_button' );

            } else {

                return this.$tc( 'subscribe.alive_subscribe_button' );

            }

        },
        dialogContents(): string[] {

            const rawContent = this.$t( 'ai.search.dialog.content' ) as unknown as string[];
            if ( this.showMoreInfo ) {

                return rawContent;

            } else {

                return rawContent.slice( 0, 1 );

            }

        },
        hasAiSubscription(): boolean {

            return this.subscriptions.some( sub => sub.aiSupport );

        },
        aiSearchCanWork(): boolean {

            return this.hasAiSubscription ;

        },
        config() {

            return config;

        },
        currentSubscription(): ICloudService | undefined {

            return this.simManager.currentSubscription;

        },
        hasDeviceCanSearch(): boolean {

            return this.deviceList.some( device =>  device.smartAiEnabled );

        },
    },
    methods: {
        getDeviceTitle,
        async handleDialogConfirm() {

            if ( !this.hasCurrentPlan ) {

                await this.$router.push({
                    path: '/user/subscribe-plan/lock/choose',
                    query: {
                        ymh: uidHandler.encrypt( this.simManager.iccid ),
                        mode: ESubscribeMode.CREATE,
                        support_ai: '1'
                    }
                });

            } else {

                await this.$router.push({
                    path: '/user/subscribe-plan/lock/choose',
                    query: {
                        ps_id: this.currentSubscription?.id,
                        ymh: uidHandler.encrypt( this.simManager.iccid ),
                        mode: ESubscribeMode.SWITCH,
                        support_ai: '1'
                    }
                });

            }

            this.handleCloseDialog();

        },
        async getSupportedDeviceList() {

            this.deviceList = ( await getSubDeviceList({
                ai_support: true
            })).map(( device ) => {

                return {
                    ...device,
                    checked: device.smartAiEnabled
                };

            });

        },
        handleCloseDialog() {

            this.$emit( 'update:showSubscriptionDialog', false );

        },
    },
});
</script>

<style lang="less" scoped>
/deep/ .dialog-content {
    font-size: 18px;
    font-weight: 500;
    line-height: 27px;
    display: flex;
    flex-direction: column;
    gap: 24px;
    .info{
        position: relative;
        display: flex;
        flex-direction: column;
        gap: 12px;
        .more-btn{
            position: absolute;
            bottom: 0;
            right: 0;
            color: @b_6;
            font-size: 16px;
            font-weight: 500;
            line-height: 27px;
            background-color: @bg_02;
            display: flex;
            align-items: center;
        }
    }
    .agreement{
        display: flex;
        gap: 12px;
        padding-top: 24px;
        border-top: 1px solid @line;
        font-size: 14px;
        line-height: 21px;
        font-weight: 500;
    }
    .device-list {
        background-color: @bg_01;
        padding: 20px;
        border-radius: @border-radius-level-2;
        max-height: 200px;
        overflow-y: auto;

        header{
            display: flex;
            justify-content: space-between;
            h4 {
                font-size: 16px;
                font-weight: 600;
                line-height: 24px;
            }

            .all {
                font-size: 14px;
                font-weight: 500;
                line-height: 24px;
                color: @b_6;
            }
        }

        ul {
            display: flex;
            flex-direction: column;
            gap: 16px;
            margin-top: 12px;

            li {
                display: flex;
                justify-content: space-between;
                align-items: center;
                font-size: 14px;
                line-height: 27px;
                font-weight: 600;
                padding-bottom: 16px;
                border-bottom: 1px solid @line;
                &:last-child {
                    border-bottom: none;
                }
            }
        }
    }

    .empty {
        color: @red_5;
    }
}
</style>
